import { SaveOutlined } from '@ant-design/icons';
import { Button, Skeleton, Space, Switch, Tooltip, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

import { Error403Unauthorized } from '@app/components';
import { FORBIDDEN_ERROR_MAIN_PART, ORGANIZATIONS_PATH } from '@app/constants';

import { useModuleAccessRightsByOrganization } from '@pages/Organizations/OrganizationSettings/hooks/useModuleAccessRightsByOrganization';

const { Text, Title } = Typography;

export const ModulesRights = () => {
  const navigate = useNavigate();
  const {
    hasAnyChanges,
    info,
    rights,
    isLoading,
    isSaving,
    changeRights,
    saveRights,
    isError,
  } = useModuleAccessRightsByOrganization();

  const cancelHandler = () => {
    navigate(ORGANIZATIONS_PATH);
  };

  const handleModuleActivationChange = (value: boolean, index: number) =>
    changeRights((prev) => {
      const newRights = [...prev];

      newRights[index] = {
        ...newRights[index],
        Allowed: value,
      };

      return newRights;
    });

  if (isLoading) {
    return <Skeleton active />;
  }

  if (isError) {
    return <Error403Unauthorized title={`${FORBIDDEN_ERROR_MAIN_PART}.`} />;
  }

  return (
    <section>
      <div className="container">
        <div className="rights">
          <Space direction="vertical" size="middle">
            <Space direction="vertical">
              <Title
                level={4}
                style={{ margin: 0 }}
                data-testid="modules-title"
              >
                Модули
              </Title>
              <div
                className="rights-description"
                data-testid="modules-description"
              >
                Здесь можно активировать модули, к которым у организации
                появится доступ.
              </div>
            </Space>

            <ul className="rights-list">
              {rights.map(({ RightType, Allowed, Name }, index) => (
                <li className="rights-item" key={RightType}>
                  <Switch
                    onChange={(value) =>
                      handleModuleActivationChange(value, index)
                    }
                    checked={Allowed}
                    data-testid={
                      Allowed ? 'active-radio-button' : 'inactive-radio-button'
                    }
                  />
                  <Text
                    className="item-label"
                    data-testid={`module-${RightType}`}
                  >
                    Модуль «{Name}»
                  </Text>
                </li>
              ))}
            </ul>
          </Space>

          <div className="buttons">
            <Tooltip
              placement="bottom"
              title={
                hasAnyChanges
                  ? ''
                  : 'Сохранение станет доступно только после изменения прав к модулям'
              }
            >
              <Button
                icon={<SaveOutlined />}
                size="large"
                type="primary"
                loading={isSaving}
                disabled={!hasAnyChanges}
                onClick={saveRights}
                data-testid="save"
              >
                Сохранить
              </Button>
            </Tooltip>
            <Button
              size="large"
              type="primary"
              ghost
              loading={isSaving}
              onClick={cancelHandler}
              data-testid="cancel"
            >
              Отменить
            </Button>
          </div>
        </div>
        <article className="info">
          <Title
            className="info-header"
            level={5}
            data-testid="organization-title"
          >
            Организация
          </Title>
          <ul className="info-content">
            <li className="info-item">
              <span className="label" data-testid="id-title">
                ID:
              </span>
              <span className="value" data-testid="organization-id">
                {info?.Id}
              </span>
            </li>
            <li className="info-item">
              <span className="label" data-testid="name-title">
                Название:
              </span>
              <span className="value" data-testid="organization-name">
                {info?.Name}
              </span>
            </li>
          </ul>
        </article>
      </div>
    </section>
  );
};
